// AdminInsights — redesigned admin dashboard overview.
// User filter in header: select any customer to scope all metrics.
// Auto-refreshes every 30 seconds.

import React, { useEffect, useState, useRef } from "react";
import {
  Briefcase, Users, Clock, DollarSign, AlertTriangle, TrendingDown,
  CheckCircle2, Activity, Loader2, ArrowDownRight, Sparkles, Bell,
  ThumbsUp, RefreshCw, UserCheck, ChevronDown,
} from "lucide-react";
import api from "../../services/api";

const WINDOWS = [
  { label: "7d",  days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "All", days: 3650 },
];

const AUTO_REFRESH_SEC = 30;

export default function AdminInsights() {
  const [days, setDays]           = useState(30);
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [users, setUsers]         = useState([]);
  const [selectedUser, setSelectedUser] = useState(null); // { _id, name, email, companyCode }
  const [showUserDrop, setShowUserDrop] = useState(false);
  const [userSearch, setUserSearch]     = useState('');
  const [countdown, setCountdown]       = useState(AUTO_REFRESH_SEC);
  const timerRef  = useRef(null);
  const countRef  = useRef(null);

  // Fetch user list for filter
  useEffect(() => {
    api.get('/admin/users').then(r => setUsers(r.data?.users || [])).catch(() => {});
  }, []);

  const load = async (d = days, user = selectedUser) => {
    setLoading(true);
    try {
      let url = `/admin/dashboard/insights?days=${d}`;
      if (user?.companyCode) url += `&companyCode=${user.companyCode}`;
      const r = await api.get(url);
      setData(r.data?.data || null);
    } catch (e) {
      console.error("insights load failed", e);
    } finally {
      setLoading(false);
    }
  };

  // Load on days / user change
  useEffect(() => {
    load(days, selectedUser);
    // eslint-disable-next-line
  }, [days, selectedUser]);

  // Auto-refresh every 30s
  useEffect(() => {
    setCountdown(AUTO_REFRESH_SEC);
    timerRef.current = setInterval(() => {
      load(days, selectedUser);
      setCountdown(AUTO_REFRESH_SEC);
    }, AUTO_REFRESH_SEC * 1000);

    countRef.current = setInterval(() => {
      setCountdown(c => (c <= 1 ? AUTO_REFRESH_SEC : c - 1));
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(countRef.current);
    };
    // eslint-disable-next-line
  }, [days, selectedUser]);

  const filteredUsers = users.filter(u =>
    !userSearch ||
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.companyName?.toLowerCase().includes(userSearch.toLowerCase())
  );

  if (loading && !data) {
    return <div className="flex items-center gap-2 text-meraki-indigo/60 p-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading insights…</div>;
  }
  if (!data) return <div className="p-10 text-meraki-coral">Couldn't load dashboard.</div>;

  const k = data.kpis;

  return (
    <div className="space-y-8 font-outfit">
      {/* Header + filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="title-meraki text-2xl">Hiring Command Center</h1>
          <p className="subtitle-meraki text-sm">
            {selectedUser
              ? `Viewing: ${selectedUser.name || selectedUser.email} · ${selectedUser.companyName || selectedUser.companyCode}`
              : `All customers`}
            {' — '}last {data.window.days >= 3650 ? "all time" : `${data.window.days} days`}.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* User filter */}
          <div className="relative">
            <button
              onClick={() => setShowUserDrop(d => !d)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-meraki-soft-blue rounded-lg text-sm hover:border-meraki-indigo transition-colors"
            >
              <UserCheck className="w-4 h-4 text-meraki-indigo" />
              <span className="max-w-[140px] truncate">
                {selectedUser ? (selectedUser.name || selectedUser.email) : 'All Customers'}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-meraki-graphite/50" />
            </button>

            {showUserDrop && (
              <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-meraki-soft-blue rounded-xl shadow-lg z-20">
                <div className="p-2 border-b border-meraki-soft-blue">
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search users…"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-meraki-soft-blue rounded-lg focus:outline-none focus:ring-2 focus:ring-meraki-indigo"
                  />
                </div>
                <div className="max-h-52 overflow-y-auto">
                  <button
                    onClick={() => { setSelectedUser(null); setShowUserDrop(false); setUserSearch(''); }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-meraki-soft-blue transition-colors ${!selectedUser ? 'font-semibold text-meraki-indigo' : 'text-meraki-graphite'}`}
                  >
                    All Customers
                  </button>
                  {filteredUsers.map(u => (
                    <button key={u._id}
                      onClick={() => { setSelectedUser(u); setShowUserDrop(false); setUserSearch(''); }}
                      className={`w-full text-left px-4 py-2.5 hover:bg-meraki-soft-blue transition-colors ${selectedUser?._id === u._id ? 'bg-meraki-soft-blue font-semibold text-meraki-indigo' : 'text-meraki-graphite'}`}
                    >
                      <div className="text-sm truncate">{u.name || u.email}</div>
                      <div className="text-xs text-meraki-graphite/50 truncate">{u.companyName || u.companyCode}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Date window */}
          <div className="flex bg-white border border-meraki-soft-blue rounded-full p-1">
            {WINDOWS.map((w) => (
              <button key={w.days} onClick={() => setDays(w.days)}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition ${days === w.days ? "bg-meraki-indigo text-white" : "text-meraki-graphite/60 hover:text-meraki-indigo"}`}>
                {w.label}
              </button>
            ))}
          </div>

          {/* Manual refresh + countdown */}
          <button
            onClick={() => { load(days, selectedUser); setCountdown(AUTO_REFRESH_SEC); }}
            className="flex items-center gap-1.5 p-2 rounded-full hover:bg-meraki-soft-blue text-meraki-indigo"
            title={`Auto-refreshes in ${countdown}s`}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="text-xs text-meraki-graphite/50">{countdown}s</span>
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        <Kpi icon={Briefcase}    label="Active Jobs"          value={k.activeJobs}                                                          tone="indigo" />
        <Kpi icon={Users}        label="Candidates in Funnel" value={k.candidatesInFunnel}                                                  tone="indigo" />
        <Kpi icon={CheckCircle2} label="Shortlisted"          value={k.shortlisted}                                                         tone="indigo" />
        <Kpi icon={Clock}        label="Avg Time-to-Hire"     value={k.avgTimeToHireDays != null ? `${k.avgTimeToHireDays}d` : "—"} hint="offer accepted − CV received" tone="indigo" />
        <Kpi icon={DollarSign}   label="Cost per Hire (AI)"   value={k.costPerHireUsd   != null ? `$${k.costPerHireUsd}` : "—"}   hint="AI spend ÷ hires"               tone="coral" />
        <Kpi icon={ThumbsUp}     label="Offer Acceptance"     value={`${k.offerAcceptanceRate}%`}  hint="accepted ÷ sent"                                                 tone="coral" />
        <Kpi icon={TrendingDown} label="Drop-off Rate"        value={`${k.dropOffRate}%`}          hint="left before finishing AI interview" tone={k.dropOffRate > 30 ? "warn" : "coral"} />
        <Kpi icon={Activity}     label="AI Completion"        value={`${k.aiCompletionRate}%`}     hint="finished ÷ invited"                 tone={k.aiCompletionRate < 70 ? "warn" : "coral"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Funnel */}
        <div className="lg:col-span-2 card-meraki p-6">
          <div className="flex items-center gap-2 mb-5">
            <Activity className="w-5 h-5 text-meraki-indigo" />
            <h2 className="text-lg font-bold text-meraki-indigo">Hiring Funnel</h2>
            <span className="text-xs text-meraki-graphite/50 ml-2">leakage % shown vs previous stage</span>
          </div>
          <div className="space-y-3">
            {data.funnel.map((f, i) => {
              const max = data.funnel[0].count || 1;
              const widthPct = Math.max(4, Math.round((f.count / max) * 100));
              const leaking = i > 0 && f.pctOfPrev < 50;
              return (
                <div key={f.stage}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-meraki-graphite">{f.stage}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-bold text-meraki-indigo">{f.count}</span>
                      {i > 0 && (
                        <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${leaking ? "text-meraki-coral" : "text-emerald-600"}`}>
                          <ArrowDownRight className="w-3 h-3" />{f.pctOfPrev}%
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="w-full h-3 bg-meraki-soft-blue rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${leaking ? "bg-meraki-coral" : "bg-meraki-indigo"}`} style={{ width: `${widthPct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Needs attention */}
        <div className="card-meraki p-6">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-meraki-coral" />
            <h2 className="text-lg font-bold text-meraki-indigo">Needs Attention</h2>
            <span className="pill-coral ml-auto">{data.attention.length}</span>
          </div>
          {data.attention.length === 0 ? (
            <div className="text-sm text-meraki-graphite/50 py-8 text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              All clear — nothing needs your attention.
            </div>
          ) : (
            <div className="space-y-3">
              {data.attention.map((a, i) => (
                <div key={i} className={`p-3 rounded-meraki border-l-4 ${a.severity === "high" ? "border-meraki-coral bg-meraki-coral-50" : "border-amber-400 bg-amber-50"}`}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${a.severity === "high" ? "text-meraki-coral" : "text-amber-500"}`} />
                    <div>
                      <p className="text-sm text-meraki-graphite leading-snug">{a.message}</p>
                      <button className="text-xs font-semibold text-meraki-indigo mt-1 hover:underline">{a.action} →</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* AI performance */}
        <div className="card-meraki p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-meraki-indigo" />
            <h2 className="text-lg font-bold text-meraki-indigo">AI Interview Performance</h2>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Mini label="Completion" value={`${data.ai.completionRate}%`} good={data.ai.completionRate >= 70} />
            <Mini label="Invited"    value={data.ai.invited} />
            <Mini label="Completed"  value={data.ai.completed} />
          </div>
          {data.ai.performance.length > 0 ? (
            <div className="space-y-2">
              {data.ai.performance.map((p) => (
                <div key={p.round} className="flex items-center justify-between text-sm">
                  <span className="capitalize text-meraki-graphite">{p.round.replace(/([A-Z])/g, " $1")}</span>
                  <span className="flex items-center gap-3">
                    <span className="text-meraki-graphite/50">{p.completed} done</span>
                    <span className={`font-bold ${p.avgScore >= 75 ? "text-emerald-600" : p.avgScore >= 50 ? "text-amber-600" : "text-meraki-coral"}`}>avg {p.avgScore}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-meraki-graphite/50">No completed AI interviews in this window.</p>}
        </div>

        {/* ROI */}
        <div className="card-meraki p-6 bg-gradient-to-br from-meraki-indigo to-meraki-indigo-700 text-white">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-meraki-coral" />
            <h2 className="text-lg font-bold">Return on Investment</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-3xl font-extrabold">{data.roi.hoursSaved}</div>
              <div className="text-xs text-white/60 mt-1">Hours saved</div>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-meraki-coral">${data.roi.aiCostUsd}</div>
              <div className="text-xs text-white/60 mt-1">AI cost</div>
            </div>
            <div>
              <div className="text-3xl font-extrabold">${data.roi.manualCostUsd.toLocaleString()}</div>
              <div className="text-xs text-white/60 mt-1">Manual equiv.</div>
            </div>
          </div>
          {data.roi.multiple != null && (
            <div className="mt-5 pt-4 border-t border-white/15 text-sm">
              <span className="text-white/70">Effective leverage: </span>
              <span className="font-bold text-meraki-coral">{data.roi.multiple}× cheaper than manual screening</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, tone = "indigo" }) {
  const toneCls = { indigo: "text-meraki-indigo", coral: "text-meraki-coral", warn: "text-amber-500" }[tone];
  return (
    <div className="card-meraki p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-meraki-graphite/50">{label}</span>
        <Icon className={`w-5 h-5 ${toneCls}`} />
      </div>
      <div className={`text-3xl font-extrabold mt-2 ${toneCls}`}>{value}</div>
      {hint && <div className="text-[11px] text-meraki-graphite/40 mt-1">{hint}</div>}
    </div>
  );
}

function Mini({ label, value, good }) {
  return (
    <div className="bg-meraki-cloud rounded-meraki p-3 text-center">
      <div className={`text-xl font-bold ${good === undefined ? "text-meraki-indigo" : good ? "text-emerald-600" : "text-meraki-coral"}`}>{value}</div>
      <div className="text-[11px] text-meraki-graphite/50 mt-0.5">{label}</div>
    </div>
  );
}
